import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { phase1Api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import PortalSidebar from "../components/PortalSidebar";
import PortalTopbar from "../components/PortalTopbar";

function money(value) {
  const amount = Number(value || 0);
  return `US$${amount.toFixed(2)}`;
}

function statusTone(value) {
  const status = String(value || "").toUpperCase();
  if (["ACTIVE", "TRIAL", "CONFIRMED", "PAID", "SETTLED"].includes(status)) return "good";
  if (["GRACE", "PENDING", "PENDING_PAYMENT", "PENDING_SETTLEMENT"].includes(status)) return "warn";
  if (["FAILED", "CANCELLED", "SUSPENDED", "REFUNDED"].includes(status)) return "bad";
  return "neutral";
}

const paymentMethods = ["PAY_ON_DELIVERY", "MANUAL", "ECOCASH", "ONEMONEY", "PAYNOW", "CARD", "BANK_TRANSFER", "OTHER"];

const paymentMethodLabels = {
  PAY_ON_DELIVERY: "Pay on delivery",
  MANUAL: "Manual / cash / bank",
  ECOCASH: "EcoCash",
  ONEMONEY: "OneMoney",
  PAYNOW: "Paynow",
  CARD: "Card",
  BANK_TRANSFER: "Bank transfer",
  OTHER: "Other",
};

function methodLabel(method) {
  return paymentMethodLabels[method] || String(method || "").replaceAll("_", " ");
}

function packageLimit(value, noun) {
  return value === null || value === undefined ? `Unlimited ${noun}` : `${value} ${noun}`;
}

function usageLimit(value) {
  return value === null || value === undefined ? "Unlimited" : value;
}

const defaultPlanForm = {
  id: null,
  code: "",
  name: "",
  audience: "SELLER",
  monthly_price: "0",
  max_products: "",
  max_services: "",
  max_branches: "",
  max_ai_messages_per_month: "",
  max_enquiries_per_month: "",
  trial_days: 30,
  grace_days: 7,
  allow_ai_assistant: true,
  allow_whatsapp_links: false,
  features: "",
  is_active: true,
  display_order: 0,
};

function normalisePlanForForm(plan = {}) {
  return {
    id: plan.id || null,
    code: plan.code || "",
    name: plan.name || "",
    audience: plan.audience || "SELLER",
    monthly_price: plan.monthly_price ?? "0",
    max_products: plan.max_products ?? "",
    max_services: plan.max_services ?? "",
    max_branches: plan.max_branches ?? "",
    max_ai_messages_per_month: plan.max_ai_messages_per_month ?? "",
    max_enquiries_per_month: plan.max_enquiries_per_month ?? "",
    trial_days: plan.trial_days ?? 30,
    grace_days: plan.grace_days ?? 7,
    allow_ai_assistant: plan.allow_ai_assistant !== false,
    allow_whatsapp_links: false,
    features: Array.isArray(plan.features) ? plan.features.join("\n") : "",
    is_active: plan.is_active !== false,
    display_order: plan.display_order ?? 0,
  };
}

function normaliseSettings(settings = {}) {
  const activeMethods = settings.active_payment_methods?.length ? settings.active_payment_methods : ["PAY_ON_DELIVERY", "ECOCASH", "ONEMONEY", "BANK_TRANSFER", "PAYNOW"];
  const cleanedActiveMethods = activeMethods.includes("PAY_ON_DELIVERY") ? activeMethods : ["PAY_ON_DELIVERY", ...activeMethods];
  return {
    default_currency: settings.default_currency || "US$",
    default_commission_rate: settings.default_commission_rate ?? "5.00",
    default_payment_method: settings.default_payment_method || "PAY_ON_DELIVERY",
    payment_collection_mode: settings.payment_collection_mode || "TAZY_COLLECTS",
    seller_settlement_mode: settings.seller_settlement_mode || "MANUAL_AFTER_DELIVERY",
    payout_hold_days: settings.payout_hold_days ?? 0,
    require_finance_confirmation: settings.require_finance_confirmation !== false,
    allow_direct_seller_payment: Boolean(settings.allow_direct_seller_payment),
    active_payment_methods: cleanedActiveMethods,
    payment_instructions: settings.payment_instructions || "",
    subscription_notes: settings.subscription_notes || "",
  };
}

function accountTitle(user) {
  const type = String(user?.account_type || "").toUpperCase();
  if (["SUPER_ADMIN", "ADMIN", "FINANCE"].includes(type) || user?.is_platform_staff || user?.is_superuser) return "Finance Reconciliation";
  if (type === "BUYER") return "My Payments";
  if (["SELLER", "BUSINESS"].includes(type)) return "Settlement Centre";
  if (type === "COURIER") return "Transport Payment Records";
  return "Finance Centre";
}

export default function FinanceWorkspace() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [payload, setPayload] = useState({ wallets: [], transactions: [], commissions: [], subscriptions: [], plans: [], totals: {}, counts: {}, settings: null });
  const [settingsForm, setSettingsForm] = useState(normaliseSettings());
  const [filters, setFilters] = useState({ q: "", status: "ALL", payment_for: "ALL" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmForm, setConfirmForm] = useState({ payment_reference: "", payment_method: "" });
  const [settleTarget, setSettleTarget] = useState(null);
  const [settleForm, setSettleForm] = useState({ payout_reference: "", notes: "" });
  const [planForm, setPlanForm] = useState(defaultPlanForm);
  const [subscriptionModal, setSubscriptionModal] = useState(null);
  const [subscriptionPaymentForm, setSubscriptionPaymentForm] = useState({ payment_method: "PAYNOW", paynow_method: "ecocash", mobile: "", payment_reference: "" });
  const [payoutModal, setPayoutModal] = useState(false);
  const [payoutForm, setPayoutForm] = useState({ wallet: "", amount: "", payout_method: "ECOCASH", account_name: "", destination: "", request_notes: "" });
  const [payoutReview, setPayoutReview] = useState(null);
  const [payoutReviewForm, setPayoutReviewForm] = useState({ action: "APPROVE", review_notes: "", payout_reference: "" });
  const [subscriptionTab, setSubscriptionTab] = useState("OVERVIEW");
  const [activationData, setActivationData] = useState({ codes: [], eligible_users: [] });
  const [activationCode, setActivationCode] = useState("");
  const [proofFiles, setProofFiles] = useState({});
  const [reviewNotes, setReviewNotes] = useState({});
  const [activationForm, setActivationForm] = useState({ mode: "DIRECT", user: "", plan: "", amount_received: "", payment_method: "CASH", payment_reference: "", duration_days: 30, expiry_days: 7, notes: "" });

  const accountType = String(user?.account_type || "").toUpperCase();
  const isStaff = user?.is_platform_staff || user?.is_superuser || ["SUPER_ADMIN", "ADMIN", "FINANCE"].includes(accountType);
  const isBuyer = accountType === "BUYER";
  const isSellerLike = ["SELLER", "BUSINESS"].includes(accountType);
  const isSubscriptionPage = location.pathname.includes("/subscriptions");
  const canRequestPayout = ["SELLER", "BUSINESS", "COURIER"].includes(accountType) && !isStaff;
  const subscriptionPaymentMethods = useMemo(() => {
    const configured = settingsForm.active_payment_methods || [];
    return configured.reduce((result, item) => {
      const raw = String(item || "").toUpperCase();
      const method = ["PAYNOW", "ECOCASH", "ONEMONEY", "CARD"].includes(raw) ? "PAYNOW" : raw;
      if (method && method !== "PAY_ON_DELIVERY" && !result.includes(method)) result.push(method);
      return result;
    }, []);
  }, [settingsForm.active_payment_methods]);

  async function load(nextFilters = filters) {
    setLoading(true);
    setError("");
    try {
      const params = isStaff ? nextFilters : {};
      const [res, activationRes] = await Promise.all([
        phase1Api.financeSummary(params),
        isSubscriptionPage && isStaff ? phase1Api.subscriptionActivationCodes() : Promise.resolve({ data: { codes: [], eligible_users: [] } }),
      ]);
      const data = res.data || {};
      setPayload(data);
      setActivationData(activationRes.data || { codes: [], eligible_users: [] });
      setSettingsForm(normaliseSettings(data.settings));
    } catch (err) {
      setError(err?.response?.data?.detail || "Finance records could not be loaded right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSubscriptionPage) setSubscriptionTab(isStaff ? "PACKAGES" : "OVERVIEW");
  }, [isSubscriptionPage, isStaff]);

  const plans = useMemo(() => {
    if (isBuyer) return [];
    if (isStaff) return payload.plans || [];
    return (payload.plans || []).filter((plan) => plan.audience === "ALL" || plan.audience === accountType);
  }, [payload.plans, accountType, isBuyer, isStaff]);

  function openConfirm(payment) {
    setConfirmTarget(payment);
    setConfirmForm({
      payment_reference: payment.payment_reference || "",
      payment_method: payment.payment_method || settingsForm.default_payment_method || "MANUAL",
    });
  }

  async function confirmPayment(event) {
    event.preventDefault();
    if (!confirmTarget?.id) return;
    setBusy(`confirm-${confirmTarget.id}`);
    setMessage("");
    setError("");
    try {
      await phase1Api.confirmPayment(confirmTarget.id, confirmForm);
      setMessage(`Payment ${confirmTarget.code} confirmed.`);
      setConfirmTarget(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Payment could not be confirmed.");
    } finally {
      setBusy("");
    }
  }

  function openSettlement(item) {
    setSettleTarget(item);
    setSettleForm({
      payout_reference: "",
      notes: `Settlement released for ${item.order_number || item.code}`,
    });
  }

  async function settleCommission(event) {
    event.preventDefault();
    if (!settleTarget?.id) return;
    setBusy(`settle-${settleTarget.id}`);
    setMessage("");
    setError("");
    try {
      await phase1Api.settleCommission(settleTarget.id, settleForm);
      setMessage(`Settlement ${settleTarget.code} released.`);
      setSettleTarget(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Settlement could not be released.");
    } finally {
      setBusy("");
    }
  }

  function choosePlan(plan, renewal = false) {
    if (Number(plan.monthly_price || 0) <= 0) {
      subscribe(plan, { payment_method: "MANUAL", renewal });
      return;
    }
    const method = subscriptionPaymentMethods.includes("PAYNOW") ? "PAYNOW" : subscriptionPaymentMethods[0] || "";
    if (!renewal && entitlement.trial_available && Number(plan.trial_days || 0) > 0) {
      subscribe(plan, { payment_method: "PAY_ON_DELIVERY" });
      return;
    }
    setSubscriptionPaymentForm({ payment_method: method, paynow_method: "ecocash", mobile: "", payment_reference: "" });
    setSubscriptionModal({ ...plan, renewal });
  }

  async function subscribe(plan, paymentDetails = {}) {
    setBusy(`plan-${plan.id}`);
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.subscribeToPlan({ plan: plan.id, ...paymentDetails });
      if (res.data?.mobile_pending && res.data?.payment_return_url) {
        window.location.assign(res.data.payment_return_url);
        return;
      }
      const subscription = res.data?.subscription;
      setMessage(subscription?.status === "TRIAL"
        ? `${plan.name} free trial has started. Its limits apply immediately.`
        : subscription?.status === "ACTIVE"
        ? `${plan.name} is now active and its limits apply immediately.`
        : `${plan.name} was selected. Payment ${res.data?.payment?.code || "confirmation"} is pending before package access starts.`);
      setSubscriptionModal(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Subscription could not be created.");
    } finally {
      setBusy("");
    }
  }

  async function submitSubscriptionPayment(event) {
    event.preventDefault();
    if (!subscriptionModal) return;
    await subscribe(subscriptionModal, { ...subscriptionPaymentForm, renewal: Boolean(subscriptionModal.renewal) });
  }

  async function redeemCode(event) {
    event.preventDefault();
    setBusy("redeem-code"); setMessage(""); setError("");
    try {
      await phase1Api.redeemSubscriptionCode({ code: activationCode });
      setActivationCode(""); setMessage("Your subscription package is now active."); await load();
    } catch (err) { setError(err?.response?.data?.detail || "The activation code could not be used."); }
    finally { setBusy(""); }
  }

  async function uploadProof(paymentId) {
    const proof = proofFiles[paymentId];
    if (!proof) { setError("Choose a PDF, JPG or PNG proof of payment first."); return; }
    setBusy(`proof-${paymentId}`); setMessage(""); setError("");
    try {
      await phase1Api.submitSubscriptionProof(paymentId, proof);
      setMessage("Proof of payment submitted for review. Package access will start only after approval."); await load();
    } catch (err) { setError(err?.response?.data?.detail || "Proof of payment could not be submitted."); }
    finally { setBusy(""); }
  }

  async function submitAdminActivation(event) {
    event.preventDefault();
    setBusy("admin-activation"); setMessage(""); setError("");
    try {
      const payload = { ...activationForm, user: Number(activationForm.user), assigned_user: Number(activationForm.user), plan: Number(activationForm.plan) };
      const res = activationForm.mode === "CODE" ? await phase1Api.createSubscriptionActivationCode(payload) : await phase1Api.adminActivateSubscription(payload);
      setMessage(activationForm.mode === "CODE" ? `Activation code ${res.data?.code} created for the selected account.` : "The paid subscription is now active.");
      setActivationForm({ mode: "DIRECT", user: "", plan: "", amount_received: "", payment_method: "CASH", payment_reference: "", duration_days: 30, expiry_days: 7, notes: "" });
      await load();
    } catch (err) { setError(err?.response?.data?.detail || "Subscription activation could not be completed."); }
    finally { setBusy(""); }
  }

  async function reviewSubscriptionPayment(payment, action) {
    const notes = reviewNotes[payment.id] || "";
    if (action === "REJECT" && !notes.trim()) { setError("Add a rejection reason before rejecting the proof."); return; }
    setBusy(`review-${payment.id}`); setMessage(""); setError("");
    try {
      await phase1Api.reviewSubscriptionPayment(payment.id, { action, notes });
      setMessage(action === "APPROVE" ? `Payment ${payment.code} approved and package activated.` : `Payment ${payment.code} rejected.`);
      await load();
    } catch (err) { setError(err?.response?.data?.detail || "The payment review could not be completed."); }
    finally { setBusy(""); }
  }

  async function updateSubscription(item, updates) {
    setBusy(`subscription-${item.id}`);
    setMessage("");
    setError("");
    try {
      await phase1Api.updateSubscriptionStatus(item.id, updates);
      setMessage(`${item.plan_name} subscription updated.`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Subscription could not be updated.");
    } finally {
      setBusy("");
    }
  }

  function openPayoutRequest() {
    const wallet = (payload.wallets || []).find((item) => Number(item.available_balance || 0) > 0 && item.status === "ACTIVE");
    setPayoutForm({ wallet: wallet ? String(wallet.id) : "", amount: "", payout_method: "ECOCASH", account_name: user?.display_name || user?.business_name || "", destination: user?.phone || "", request_notes: "" });
    setPayoutModal(true);
  }

  async function submitPayoutRequest(event) {
    event.preventDefault();
    setBusy("payout-create");
    setMessage("");
    setError("");
    try {
      const res = await phase1Api.createPayoutRequest({ ...payoutForm, wallet: Number(payoutForm.wallet), amount: String(payoutForm.amount) });
      setMessage(`Payout ${res.data?.code || "request"} submitted for finance review.`);
      setPayoutModal(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.amount || "Payout request could not be submitted.");
    } finally {
      setBusy("");
    }
  }

  function openPayoutReview(item, action) {
    setPayoutReview(item);
    setPayoutReviewForm({ action, review_notes: "", payout_reference: "" });
  }

  async function decidePayout(event) {
    event.preventDefault();
    if (!payoutReview) return;
    setBusy(`payout-${payoutReview.id}`);
    setMessage("");
    setError("");
    try {
      await phase1Api.decidePayoutRequest(payoutReview.id, payoutReviewForm);
      setMessage(`Payout ${payoutReview.code} updated.`);
      setPayoutReview(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.response?.data?.payout_reference || "Payout request could not be updated.");
    } finally {
      setBusy("");
    }
  }

  function editPlan(plan) {
    setPlanForm(normalisePlanForForm(plan));
  }

  async function savePlan(event) {
    event.preventDefault();
    setBusy("plan-save");
    setMessage("");
    setError("");
    const body = {
      ...planForm,
      code: String(planForm.code || "").trim().toUpperCase(),
      name: String(planForm.name || "").trim(),
      monthly_price: String(planForm.monthly_price || "0"),
      max_products: planForm.max_products === "" ? null : Number(planForm.max_products),
      max_services: planForm.max_services === "" ? null : Number(planForm.max_services),
      max_branches: planForm.max_branches === "" ? null : Number(planForm.max_branches),
      max_ai_messages_per_month: planForm.max_ai_messages_per_month === "" ? null : Number(planForm.max_ai_messages_per_month),
      max_enquiries_per_month: planForm.max_enquiries_per_month === "" ? null : Number(planForm.max_enquiries_per_month),
      trial_days: Number(planForm.trial_days || 0),
      grace_days: Number(planForm.grace_days || 0),
      features: String(planForm.features || "").split("\n").map((item) => item.trim()).filter(Boolean),
      display_order: Number(planForm.display_order || 0),
    };
    try {
      if (planForm.id) {
        await phase1Api.updateSubscriptionPlan(planForm.id, body);
        setMessage("Subscription package updated.");
      } else {
        await phase1Api.createSubscriptionPlan(body);
        setMessage("Subscription package created.");
      }
      setPlanForm(defaultPlanForm);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Subscription package could not be saved.");
    } finally {
      setBusy("");
    }
  }

  function toggleMethod(method) {
    setSettingsForm((current) => {
      const list = current.active_payment_methods || [];
      return {
        ...current,
        active_payment_methods: list.includes(method) ? list.filter((item) => item !== method) : [...list, method],
      };
    });
  }

  async function saveSettings() {
    setBusy("settings");
    setMessage("");
    setError("");
    try {
      const body = {
        ...settingsForm,
        default_commission_rate: String(settingsForm.default_commission_rate || "0"),
        payout_hold_days: Number(settingsForm.payout_hold_days || 0),
      };
      const res = await phase1Api.updateFinanceSettings(body);
      setSettingsForm(normaliseSettings(res.data));
      setMessage("Finance settings saved.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Finance settings could not be saved.");
    } finally {
      setBusy("");
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    load(filters);
  }

  const heroTitle = isSubscriptionPage ? "Subscription Centre" : accountTitle(user);
  const payments = payload.transactions || [];
  const entitlement = payload.entitlement || {};
  const subscriptions = payload.subscriptions || [];
  const payoutRequests = payload.payout_requests || [];
  const activePlanId = entitlement.plan?.id;
  const pendingPlanIds = new Set(subscriptions.filter((item) => item.status === "PENDING_PAYMENT").map((item) => item.plan));
  const pendingSubscriptionPayments = payments.filter((item) => item.payment_for === "SUBSCRIPTION" && item.status === "PENDING");

  return (
    <div className={`portal-shell-v2 refined-portal-shell ${collapsed ? "is-collapsed" : ""}`}>
      <PortalSidebar collapsed={collapsed} onToggle={() => setCollapsed((value) => !value)} />
      <main className="portal-main-v2 finance-workspace-main finance-role-clean">
        <PortalTopbar user={user} onLogout={logout} />

        <section className="finance-hero-card finance-hero-card--clean">
          <div>
            <span>{isSubscriptionPage ? "MONTHLY BUSINESS ACCESS" : "MARKETPLACE FINANCE"}</span>
            <h1>{heroTitle}</h1>
            <p>{isSubscriptionPage ? (isStaff ? "Set up packages, review access status and manage renewals." : "See what your package includes, monitor usage and renew without mixing it with payment and wallet records.") : isStaff ? "Review payments, match references, confirm receipts and control marketplace money rules." : isBuyer ? "Track your order payments and payment status." : "Track marketplace settlements, payouts and payment records."}</p>
          </div>
          {!isSubscriptionPage && <div className="finance-hero-stats">
            <div><strong>{payload.counts?.pending_payments || 0}</strong><span>Pending</span></div>
            <div><strong>{payload.counts?.confirmed_payments || 0}</strong><span>Confirmed</span></div>
          </div>}
        </section>

        {message && <div className="success-box workspace-alert">{message}</div>}
        {error && <div className="error-box workspace-alert">{error}</div>}

        {!isSubscriptionPage && isStaff && (
          <section className="finance-settings-panel compact-finance-settings">
            <div className="finance-settings-panel__intro">
              <span>FINANCIAL SETTINGS</span>
              <h3>Marketplace money rules</h3>
            </div>
            <div className="finance-settings-grid">
              <label><span>Commission rate (%)</span><input type="number" min="0" max="100" step="0.01" value={settingsForm.default_commission_rate} onChange={(e) => setSettingsForm({ ...settingsForm, default_commission_rate: e.target.value })} /></label>
              <label><span>Currency</span><input value={settingsForm.default_currency} onChange={(e) => setSettingsForm({ ...settingsForm, default_currency: e.target.value })} /></label>
              <label><span>Default payment method</span><select value={settingsForm.default_payment_method} onChange={(e) => setSettingsForm({ ...settingsForm, default_payment_method: e.target.value })}>{paymentMethods.map((method) => <option key={method} value={method}>{methodLabel(method)}</option>)}</select></label>
              <label><span>Money collection</span><select value={settingsForm.payment_collection_mode} onChange={(e) => setSettingsForm({ ...settingsForm, payment_collection_mode: e.target.value })}><option value="TAZY_COLLECTS">All payments through TAZY</option><option value="HYBRID">Hybrid</option><option value="DIRECT_TO_SELLER">Direct to seller</option></select></label>
              <label><span>Seller settlement</span><select value={settingsForm.seller_settlement_mode} onChange={(e) => setSettingsForm({ ...settingsForm, seller_settlement_mode: e.target.value })}><option value="MANUAL_AFTER_DELIVERY">Manual after delivery</option><option value="MANUAL_AFTER_CONFIRMATION">Manual after payment confirmation</option><option value="HOLD_THEN_SETTLE">Hold then settle</option></select></label>
              <label><span>Payout hold days</span><input type="number" min="0" value={settingsForm.payout_hold_days} onChange={(e) => setSettingsForm({ ...settingsForm, payout_hold_days: e.target.value })} /></label>
            </div>
            <div className="finance-methods-row"><span>Active payment methods</span>{paymentMethods.map((method) => <button type="button" key={method} className={(settingsForm.active_payment_methods || []).includes(method) ? "is-active" : ""} onClick={() => toggleMethod(method)}>{methodLabel(method)}</button>)}</div>
            <div className="finance-settings-actions"><label className="settings-checkbox"><input type="checkbox" checked={settingsForm.require_finance_confirmation} onChange={(e) => setSettingsForm({ ...settingsForm, require_finance_confirmation: e.target.checked })} /> Finance confirms receipts</label><label className="settings-checkbox"><input type="checkbox" checked={settingsForm.allow_direct_seller_payment} onChange={(e) => setSettingsForm({ ...settingsForm, allow_direct_seller_payment: e.target.checked })} /> Allow direct seller payments</label><button type="button" onClick={saveSettings} disabled={busy === "settings"}>{busy === "settings" ? "Saving..." : "Save settings"}</button></div>
          </section>
        )}

        {!isSubscriptionPage && <section className={`finance-kpi-grid ${isBuyer ? "buyer-finance-kpis" : ""}`}>
          {isBuyer ? (
            <>
              <article><span>Pending amount</span><strong>{money(payload.totals?.pending_amount)}</strong></article>
              <article><span>Confirmed payments</span><strong>{money(payload.totals?.payments_confirmed)}</strong></article>
              <article><span>Payment records</span><strong>{payload.counts?.transactions || 0}</strong></article>
              <article><span>Receipts confirmed</span><strong>{payload.counts?.confirmed_payments || 0}</strong></article>
            </>
          ) : isSellerLike ? (
            <>
              <article><span>Confirmed sales</span><strong>{money(payload.totals?.payments_confirmed)}</strong></article>
              <article><span>Pending settlement</span><strong>{money(payload.totals?.seller_pending)}</strong></article>
              <article><span>Commission deducted</span><strong>{money(payload.totals?.commission_confirmed)}</strong></article>
              <article><span>Active subscriptions</span><strong>{payload.counts?.active_subscriptions || 0}</strong></article>
            </>
          ) : (
            <>
              <article><span>Confirmed through TAZY</span><strong>{money(payload.totals?.payments_confirmed)}</strong></article>
              <article><span>TAZY commission</span><strong>{money(payload.totals?.commission_confirmed)}</strong></article>
              <article><span>Seller settlement</span><strong>{money(payload.totals?.seller_pending)}</strong></article>
              <article><span>Active subscriptions</span><strong>{payload.counts?.active_subscriptions || 0}</strong></article>
            </>
          )}
        </section>}

        {!isSubscriptionPage && isStaff && (
          <section className="finance-contract-report-grid" aria-label="Subscription and marketplace finance reports">
            <article><span>Subscription revenue</span><strong>{money(payload.totals?.subscription_revenue)}</strong><small>Confirmed package payments this month</small></article>
            <article><span>Marketplace revenue</span><strong>{money(payload.totals?.marketplace_revenue)}</strong><small>Commission recorded this month</small></article>
            <article><span>Renewals due</span><strong>{payload.counts?.renewals_due || 0}</strong><small>Active packages ending within 7 days</small></article>
            <article><span>Payout queue</span><strong>{payload.counts?.pending_payouts || 0}</strong><small>{money(payload.totals?.payouts_requested)} awaiting completion</small></article>
          </section>
        )}

        {!isSubscriptionPage && <section className="finance-panel-card payment-reconciliation-card">
          <div className="records-card__header finance-table-header-clean">
            <div><span>{isStaff ? "PAYMENT RECONCILIATION" : "PAYMENTS"}</span><h3>{isStaff ? "Match payment to buyer, order and seller" : "Payment transactions"}</h3></div>
            <button onClick={() => load()}>Refresh</button>
          </div>

          {isStaff && (
            <form className="finance-recon-filters" onSubmit={applyFilters}>
              <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search payment code, order, buyer, seller or reference" />
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="ALL">All statuses</option><option value="PENDING">Pending</option><option value="CONFIRMED">Confirmed</option><option value="FAILED">Failed</option><option value="CANCELLED">Cancelled</option></select>
              <select value={filters.payment_for} onChange={(e) => setFilters({ ...filters, payment_for: e.target.value })}><option value="ALL">All payment types</option><option value="ORDER">Orders</option><option value="DELIVERY">Transport services</option><option value="SUBSCRIPTION">Subscriptions</option><option value="OTHER">Other</option></select>
              <button type="submit">Apply</button>
            </form>
          )}

          {loading ? <div className="records-empty">Loading payments...</div> : payments.length === 0 ? <div className="records-empty">No payments recorded yet.</div> : (
            <div className="finance-recon-table">
              <div className="finance-recon-head"><span>Payment</span><span>Order / purpose</span><span>Payer</span><span>Beneficiary</span><span>Method / reference</span><span>Amount</span><span>Status</span><span></span></div>
              {payments.map((tx) => (
                <article key={tx.id} className="finance-recon-row">
                  <div><strong>{tx.code}</strong><small>{new Date(tx.created_at).toLocaleString()}</small></div>
                  <div><strong>{tx.delivery_code || tx.order_number || tx.subscription_plan_name || tx.payment_for}</strong><small>{tx.payment_for === "DELIVERY" ? `Transport for ${tx.order_number}` : tx.order_items_summary || tx.payment_for}</small></div>
                  <div><strong>{tx.payer_name || "—"}</strong><small>Payer</small></div>
                  <div><strong>{tx.seller_name || "—"}</strong><small>{tx.payment_for === "DELIVERY" ? "Transporter" : tx.seller_name ? "Seller" : "TAZY"}</small></div>
                  <div><strong>{methodLabel(tx.payment_method)}</strong><small>{tx.payment_reference || "No reference yet"}</small></div>
                  <div><strong>{money(tx.amount)}</strong>{!isBuyer && <small>{isStaff || isSellerLike ? `Seller: ${money(tx.seller_settlement_amount)}` : ""}</small>}</div>
                  <div><span className={`status-chip ${statusTone(tx.status)}`}>{tx.status}</span></div>
                  <div>{isStaff && tx.status === "PENDING" ? <button type="button" className="confirm-payment-btn" onClick={() => openConfirm(tx)}>Review</button> : null}</div>
                </article>
              ))}
            </div>
          )}
        </section>}

        {!isSubscriptionPage && !isBuyer && (
          <section className="finance-grid-layout finance-grid-layout--bottom role-finance-secondary">
            <div className="finance-panel-card">
              <div className="records-card__header"><div><span>WALLETS</span><h3>{isStaff ? "Wallet balances" : "My balance"}</h3></div></div>
              <div className="wallet-card-list">
                {(payload.wallets || []).length ? payload.wallets.map((wallet) => <article key={wallet.id}><strong>{wallet.owner_name || "TAZY PLATFORM"}</strong><span>{wallet.wallet_type}</span><div><b>{money(wallet.available_balance)}</b><em>available</em></div><div><b>{money(wallet.pending_balance)}</b><em>pending settlement</em></div><div><b>{money(wallet.reserved_balance)}</b><em>reserved for payout</em></div></article>) : <div className="records-empty">No wallet balances yet.</div>}
              </div>
              {canRequestPayout && <button type="button" className="wallet-payout-button" onClick={openPayoutRequest} disabled={!(payload.wallets || []).some((wallet) => Number(wallet.available_balance || 0) > 0)}>Request payout</button>}
            </div>

            {(isStaff || isSellerLike) && (
              <div className="finance-panel-card">
                <div className="records-card__header"><div><span>COMMISSION</span><h3>{isStaff ? "Commission records" : "Commission deductions"}</h3></div></div>
                <div className="finance-table-list compact-finance-list">
                  {(payload.commissions || []).length ? payload.commissions.map((item) => (
                    <article key={item.id} className="settlement-record-row">
                      <div>
                        <strong>{item.code}</strong>
                        <span>{item.order_number} • {item.seller_name}</span>
                        {isStaff && <small>Seller settlement: {money(item.seller_amount)}</small>}
                      </div>
                      <b>{isStaff ? money(item.seller_amount) : money(item.commission_amount)}</b>
                      <span className={`status-chip ${statusTone(item.status)}`}>{item.status}</span>
                      {isStaff && item.status === "PENDING_SETTLEMENT" && (
                        <button type="button" className="confirm-payment-btn settle-action-btn" onClick={() => openSettlement(item)}>
                          Settle
                        </button>
                      )}
                    </article>
                  )) : <div className="records-empty">No settlement records yet.</div>}
                </div>
              </div>
            )}
          </section>
        )}

        {!isSubscriptionPage && !isBuyer && (
          <section className="finance-panel-card payout-management-panel">
            <div className="records-card__header">
              <div><span>PAYOUTS</span><h3>{isStaff ? "Payout approval and payment queue" : "My payout activity"}</h3></div>
              {canRequestPayout && <button type="button" onClick={openPayoutRequest}>Request payout</button>}
            </div>
            <div className="payout-table">
              {payoutRequests.length ? payoutRequests.map((item) => (
                <article key={item.id} className="payout-row">
                  <div><strong>{item.code}</strong><small>{new Date(item.created_at).toLocaleString()}</small></div>
                  {isStaff && <div><strong>{item.requester_name}</strong><small>{item.requester_email}</small></div>}
                  <div><strong>{money(item.amount)}</strong><small>{methodLabel(item.payout_method)} • {item.destination}</small></div>
                  <div><span className={`status-chip ${statusTone(item.status)}`}>{item.status}</span><small>{item.payout_reference || "No payout reference"}</small></div>
                  <div className="payout-actions">
                    {isStaff && item.status === "PENDING" && <><button type="button" onClick={() => openPayoutReview(item, "APPROVE")}>Approve</button><button type="button" className="danger" onClick={() => openPayoutReview(item, "REJECT")}>Reject</button></>}
                    {isStaff && item.status === "APPROVED" && <><button type="button" onClick={() => openPayoutReview(item, "MARK_PAID")}>Mark paid</button><button type="button" className="danger" onClick={() => openPayoutReview(item, "REJECT")}>Reject</button></>}
                    {!isStaff && item.status === "PENDING" && <button type="button" className="danger" onClick={() => openPayoutReview(item, "CANCEL")}>Cancel request</button>}
                  </div>
                </article>
              )) : <div className="records-empty">No payout requests recorded yet.</div>}
            </div>
          </section>
        )}

        {!isSubscriptionPage && !isBuyer && (payload.wallets || []).some((wallet) => (wallet.ledger_entries || []).length > 0) && (
          <section className="finance-panel-card wallet-history-panel">
            <div className="records-card__header"><div><span>TRANSACTION HISTORY</span><h3>{isStaff ? "Wallet audit records" : "My wallet activity"}</h3></div></div>
            <div className="wallet-ledger-table">
              {(payload.wallets || []).flatMap((wallet) => (wallet.ledger_entries || []).map((entry) => ({ ...entry, wallet_code: wallet.code, wallet_owner: wallet.owner_name || "TAZY PLATFORM" }))).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 120).map((entry) => (
                <article key={`${entry.wallet_code}-${entry.id}`}>
                  <div><strong>{entry.reference || entry.transaction_code || entry.commission_code || "Wallet entry"}</strong><small>{new Date(entry.created_at).toLocaleString()}</small></div>
                  {isStaff && <div><strong>{entry.wallet_owner}</strong><small>{entry.wallet_code}</small></div>}
                  <div><strong>{String(entry.entry_type || "").replaceAll("_", " ")}</strong><small>{entry.notes || "Recorded wallet activity"}</small></div>
                  <div className={entry.direction === "DEBIT" ? "ledger-debit" : "ledger-credit"}><strong>{entry.direction === "DEBIT" ? "−" : "+"}{money(entry.amount)}</strong><small>{entry.direction}</small></div>
                </article>
              ))}
            </div>
          </section>
        )}

        {isSubscriptionPage && !isBuyer && (
          <section className="finance-panel-card subscriptions-clean-panel">
            <div className="records-card__header"><div><span>SUBSCRIPTIONS</span><h3>{isStaff ? "Package setup and access" : "Plans and access"}</h3></div></div>
            <div className="subscription-page-tabs" role="tablist">
              {(isStaff ? [["PACKAGES", "Packages"], ["ACTIVATION", "Activation & review"], ["RECORDS", "Records"]] : [["OVERVIEW", "My access"], ["PACKAGES", "Packages"], ["PAYMENT", "Payment & code"], ["RECORDS", "History"]]).map(([value, label]) => (
                <button type="button" key={value} className={subscriptionTab === value ? "active" : ""} onClick={() => setSubscriptionTab(value)}>{label}</button>
              ))}
            </div>

            {!isStaff && subscriptionTab === "OVERVIEW" && (
              <div className={`current-entitlement-card ${["ACTIVE", "TRIAL"].includes(entitlement.status) ? "is-active" : entitlement.status === "GRACE" ? "is-grace" : "needs-plan"}`}>
                <div>
                  <span>{entitlement.status === "TRIAL" ? "FREE TRIAL" : entitlement.status === "ACTIVE" ? "CURRENT PACKAGE" : entitlement.status === "GRACE" ? "RENEWAL GRACE" : "PACKAGE REQUIRED"}</span>
                  <h4>{entitlement.plan?.name || "No active package"}</h4>
                  <p>{entitlement.message}</p>
                </div>
                <div className="entitlement-usage-grid">
                  {accountType === "COURIER" ? <div><strong>{entitlement.service_used || 0} / {usageLimit(entitlement.service_limit)}</strong><span>Active services</span></div> : <div><strong>{entitlement.product_used || 0} / {usageLimit(entitlement.product_limit)}</strong><span>Product listings</span></div>}
                  {accountType === "BUSINESS" && <div><strong>{entitlement.branch_used || 0} / {usageLimit(entitlement.branch_limit)}</strong><span>Active branches</span></div>}
                  <div><strong>{entitlement.enquiry_used_this_month || 0} / {usageLimit(entitlement.enquiry_limit)}</strong><span>Enquiry responses this month</span></div>
                  <div><strong>{entitlement.ai_used_this_month || 0} / {entitlement.ai_allowed ? usageLimit(entitlement.ai_limit) : 0}</strong><span>AI messages this month</span></div>
                  <div><strong>Included</strong><span>Protected TAZY CART enquiries</span></div>
                </div>
                <p className="subscription-commission-note">A package controls marketplace tools and monthly limits. Marketplace commission is separate and is deducted only from completed sales.</p>
              </div>
            )}

            {isStaff && subscriptionTab === "PACKAGES" && (
              <form className="plan-setup-form" onSubmit={savePlan}>
                <div className="plan-setup-grid">
                  <label><span>Code</span><input value={planForm.code} onChange={(e) => setPlanForm({ ...planForm, code: e.target.value })} placeholder="STARTER" required /></label>
                  <label><span>Name</span><input value={planForm.name} onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })} placeholder="Starter Seller" required /></label>
                  <label><span>Audience</span><select value={planForm.audience} onChange={(e) => setPlanForm({ ...planForm, audience: e.target.value })}><option value="SELLER">Seller</option><option value="BUSINESS">Business</option><option value="COURIER">Courier</option><option value="ALL">All</option></select></label>
                  <label><span>Monthly price</span><input type="number" min="0" step="0.01" value={planForm.monthly_price} onChange={(e) => setPlanForm({ ...planForm, monthly_price: e.target.value })} /></label>
                  <label><span>Product listing limit</span><input type="number" min="0" value={planForm.max_products} onChange={(e) => setPlanForm({ ...planForm, max_products: e.target.value })} placeholder="Blank = unlimited" /></label>
                  <label><span>Transport service limit</span><input type="number" min="0" value={planForm.max_services} onChange={(e) => setPlanForm({ ...planForm, max_services: e.target.value })} placeholder="Blank = listing limit" /></label>
                  <label><span>Business branch limit</span><input type="number" min="0" value={planForm.max_branches} onChange={(e) => setPlanForm({ ...planForm, max_branches: e.target.value })} placeholder="Blank = unlimited" /></label>
                  <label><span>AI monthly limit</span><input type="number" min="0" value={planForm.max_ai_messages_per_month} onChange={(e) => setPlanForm({ ...planForm, max_ai_messages_per_month: e.target.value })} placeholder="Blank = flexible" /></label>
                  <label><span>Enquiry monthly limit</span><input type="number" min="0" value={planForm.max_enquiries_per_month} onChange={(e) => setPlanForm({ ...planForm, max_enquiries_per_month: e.target.value })} placeholder="Blank = flexible" /></label>
                  <label><span>First trial days</span><input type="number" min="0" value={planForm.trial_days} onChange={(e) => setPlanForm({ ...planForm, trial_days: e.target.value })} /></label>
                  <label><span>Renewal grace days</span><input type="number" min="0" value={planForm.grace_days} onChange={(e) => setPlanForm({ ...planForm, grace_days: e.target.value })} /></label>
                  <label><span>Display order</span><input type="number" min="0" value={planForm.display_order} onChange={(e) => setPlanForm({ ...planForm, display_order: e.target.value })} /></label>
                  <label className="plan-features-field"><span>Other included features</span><textarea rows="4" value={planForm.features} onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })} placeholder="Enter one feature per line" /></label>
                </div>
                <div className="plan-switch-row">
                  <label><input type="checkbox" checked={planForm.allow_ai_assistant} onChange={(e) => setPlanForm({ ...planForm, allow_ai_assistant: e.target.checked })} /> AI included</label>
                  <label><input type="checkbox" checked={planForm.is_active} onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })} /> Active</label>
                  <button type="button" onClick={() => setPlanForm(defaultPlanForm)}>Clear</button>
                  <button type="submit" disabled={busy === "plan-save"}>{busy === "plan-save" ? "Saving..." : planForm.id ? "Update package" : "Create package"}</button>
                </div>
              </form>
            )}

            {subscriptionTab === "PACKAGES" && <div className="plan-card-grid">
              {plans.map((plan) => (
                <article key={plan.id}>
                  <span>{plan.audience}</span>
                  <h4>{plan.name}</h4>
                  <strong>{money(plan.monthly_price)} / month</strong>
                  <ul className="plan-benefit-list">
                    <li>{accountType === "COURIER" ? packageLimit(plan.max_services ?? plan.max_products, "active transporter services") : packageLimit(plan.max_products, "active product listings")}</li>
                    {accountType === "BUSINESS" && <li>{packageLimit(plan.max_branches, "active branches")}</li>}
                    <li>{packageLimit(plan.max_enquiries_per_month, "enquiry responses each month")}</li>
                    <li>{plan.allow_ai_assistant ? packageLimit(plan.max_ai_messages_per_month, "AI messages each month") : "AI assistant not included"}</li>
                    {(plan.features || []).map((feature) => <li key={feature}>{feature}</li>)}
                    <li>{plan.trial_days || 0}-day first trial • {plan.grace_days || 0}-day renewal grace</li>
                  </ul>
                  {isStaff ? <button type="button" onClick={() => editPlan(plan)}>Edit package</button> : activePlanId === plan.id ? <button type="button" disabled>Current package</button> : pendingPlanIds.has(plan.id) ? <button type="button" onClick={() => choosePlan(plan)}>Continue payment</button> : <button disabled={busy === `plan-${plan.id}`} onClick={() => choosePlan(plan)}>{busy === `plan-${plan.id}` ? "Selecting..." : entitlement.trial_available && Number(plan.trial_days || 0) > 0 ? "Start free trial" : "Choose package"}</button>}
                </article>
              ))}
              {plans.length === 0 && <div className="records-empty">No package is available for this account type yet. Contact TAZY support.</div>}
            </div>}

            {!isStaff && subscriptionTab === "PAYMENT" && (
              <div className="subscription-payment-grid">
                <form className="subscription-action-card" onSubmit={redeemCode}>
                  <span>ACTIVATION CODE</span><h4>Activate a paid package</h4>
                  <p>Use the single-use code issued to this account after an approved cash or bank payment.</p>
                  <label><span>Activation code</span><input required value={activationCode} onChange={(event) => setActivationCode(event.target.value.toUpperCase())} placeholder="TAZY-XXXXXX-XXXX" /></label>
                  <button type="submit" disabled={busy === "redeem-code"}>{busy === "redeem-code" ? "Activating..." : "Activate package"}</button>
                </form>
                <div className="subscription-action-card">
                  <span>PROOF OF PAYMENT</span><h4>Pending bank or manual payments</h4>
                  {pendingSubscriptionPayments.length ? pendingSubscriptionPayments.map((payment) => (
                    <div className="subscription-proof-row" key={payment.id}>
                      <div><strong>{payment.code} · {money(payment.amount)}</strong><small>{payment.subscription_plan_name} · {payment.proof_submitted_at ? "Proof awaiting review" : "Proof required"}</small></div>
                      {!payment.proof_submitted_at && <><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(event) => setProofFiles((current) => ({ ...current, [payment.id]: event.target.files?.[0] }))} /><button type="button" disabled={busy === `proof-${payment.id}`} onClick={() => uploadProof(payment.id)}>{busy === `proof-${payment.id}` ? "Uploading..." : "Submit proof"}</button></>}
                    </div>
                  )) : <div className="records-empty">No manual subscription payment is waiting for proof.</div>}
                </div>
              </div>
            )}

            {isStaff && subscriptionTab === "ACTIVATION" && (
              <div className="subscription-payment-grid">
                <form className="subscription-action-card" onSubmit={submitAdminActivation}>
                  <span>OFFLINE PAYMENT</span><h4>Activate or issue a code</h4>
                  <div className="plan-setup-grid compact-activation-grid">
                    <label><span>Action</span><select value={activationForm.mode} onChange={(e) => setActivationForm({ ...activationForm, mode: e.target.value })}><option value="DIRECT">Activate account now</option><option value="CODE">Issue account-bound code</option></select></label>
                    <label><span>Seller / business / transporter</span><select required value={activationForm.user} onChange={(e) => setActivationForm({ ...activationForm, user: e.target.value })}><option value="">Choose account</option>{(activationData.eligible_users || []).map((item) => <option key={item.id} value={item.id}>{item.name} · {item.account_type}</option>)}</select></label>
                    <label><span>Package</span><select required value={activationForm.plan} onChange={(e) => setActivationForm({ ...activationForm, plan: e.target.value })}><option value="">Choose package</option>{plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}</select></label>
                    <label><span>Amount received</span><input type="number" min="0" step="0.01" required value={activationForm.amount_received} onChange={(e) => setActivationForm({ ...activationForm, amount_received: e.target.value })} /></label>
                    <label><span>Method</span><select value={activationForm.payment_method} onChange={(e) => setActivationForm({ ...activationForm, payment_method: e.target.value })}><option value="CASH">Cash</option><option value="BANK_TRANSFER">Bank transfer</option><option value="MANUAL">Other verified payment</option></select></label>
                    <label><span>Receipt / bank reference</span><input value={activationForm.payment_reference} onChange={(e) => setActivationForm({ ...activationForm, payment_reference: e.target.value })} /></label>
                    <label><span>Access days</span><input type="number" min="1" max="366" value={activationForm.duration_days} onChange={(e) => setActivationForm({ ...activationForm, duration_days: e.target.value })} /></label>
                    {activationForm.mode === "CODE" && <label><span>Code expires after</span><input type="number" min="1" max="90" value={activationForm.expiry_days} onChange={(e) => setActivationForm({ ...activationForm, expiry_days: e.target.value })} /></label>}
                  </div>
                  <button type="submit" disabled={busy === "admin-activation"}>{busy === "admin-activation" ? "Saving..." : activationForm.mode === "CODE" ? "Issue activation code" : "Confirm payment and activate"}</button>
                </form>
                <div className="subscription-action-card">
                  <span>PAYMENT REVIEW</span><h4>Proofs awaiting a decision</h4>
                  {pendingSubscriptionPayments.length ? pendingSubscriptionPayments.map((payment) => (
                    <div className="subscription-proof-row admin-proof-row" key={payment.id}>
                      <div><strong>{payment.code} · {payment.payer_name}</strong><small>{money(payment.amount)} · {payment.proof_submitted_at ? "Proof submitted" : "No proof uploaded"}</small></div>
                      {payment.proof_url && <a href={payment.proof_url} target="_blank" rel="noreferrer">View proof</a>}
                      <input value={reviewNotes[payment.id] || ""} onChange={(event) => setReviewNotes((current) => ({ ...current, [payment.id]: event.target.value }))} placeholder="Approval note or required rejection reason" />
                      <div><button type="button" disabled={busy === `review-${payment.id}`} onClick={() => reviewSubscriptionPayment(payment, "APPROVE")}>Approve</button><button type="button" className="danger" disabled={busy === `review-${payment.id}`} onClick={() => reviewSubscriptionPayment(payment, "REJECT")}>Reject</button></div>
                    </div>
                  )) : <div className="records-empty">No subscription payment is awaiting review.</div>}
                </div>
                <div className="subscription-action-card activation-code-list">
                  <span>RECENT CODES</span><h4>Account-bound activation codes</h4>
                  {(activationData.codes || []).length ? activationData.codes.map((item) => <div key={item.id}><strong>{item.code}</strong><small>{item.assigned_user_name} · {item.plan_name} · {item.status}</small></div>) : <div className="records-empty">No activation codes issued yet.</div>}
                </div>
              </div>
            )}

            {subscriptionTab === "RECORDS" && <div className="subscription-records-block">
              <div className="records-card__header"><div><span>MONTHLY RECORDS</span><h3>{isStaff ? "Subscribed businesses and renewal tracking" : "My subscription history"}</h3></div></div>
              <div className="subscription-record-list">
                {subscriptions.length ? subscriptions.map((item) => (
                  <article key={item.id}>
                    <div><strong>{item.plan_name}</strong><small>{isStaff ? `${item.user_name} • ${item.user_email}` : item.latest_payment_code || "No payment reference"}</small></div>
                    <div><strong>{item.is_trial ? "Free trial" : money(item.amount)}</strong><small>{item.is_trial ? "First trial — no payment charged" : item.latest_payment_status || "No payment"}</small></div>
                    <div><strong>{item.starts_at || "Not started"} — {item.status === "GRACE" ? item.grace_ends_at : item.ends_at || "No end date"}</strong><small>{item.lifecycle_message}</small></div>
                    <div><span className={`status-chip ${statusTone(item.status)}`}>{item.status}</span></div>
                    <div className="subscription-record-actions">
                      {isStaff ? <>
                        {!['ACTIVE', 'TRIAL'].includes(item.status) && <button type="button" disabled={busy === `subscription-${item.id}`} onClick={() => updateSubscription(item, { status: "ACTIVE" })}>Activate</button>}
                        {['ACTIVE', 'TRIAL', 'GRACE'].includes(item.status) && <button type="button" disabled={busy === `subscription-${item.id}`} onClick={() => updateSubscription(item, { status: "SUSPENDED" })}>Suspend</button>}
                        {!['CANCELLED', 'EXPIRED'].includes(item.status) && <button type="button" className="danger" disabled={busy === `subscription-${item.id}`} onClick={() => updateSubscription(item, { status: "CANCELLED" })}>Cancel</button>}
                      </> : <>
                        {item.can_renew && <button type="button" onClick={() => choosePlan(plans.find((plan) => plan.id === item.plan) || { id: item.plan, name: item.plan_name, monthly_price: item.amount }, true)}>Renew package</button>}
                        <label><input type="checkbox" checked={Boolean(item.auto_renew)} onChange={(event) => updateSubscription(item, { auto_renew: event.target.checked })} /> Renewal reminder</label>
                      </>}
                    </div>
                  </article>
                )) : <div className="records-empty">No subscription records yet.</div>}
              </div>
            </div>}
          </section>
        )}
      </main>

      {subscriptionModal && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card payment-choice-modal">
            <header className="tazy-modal-header"><div><span>PACKAGE PAYMENT</span><h3>Activate {subscriptionModal.name}</h3></div><button type="button" onClick={() => setSubscriptionModal(null)}>×</button></header>
            <form className="payment-choice-body" onSubmit={submitSubscriptionPayment}>
              <div className="payment-summary-box"><div><span>Monthly package price</span><strong>{money(subscriptionModal.monthly_price)}</strong></div><p>Package access starts only after this payment is confirmed.</p></div>
              <div className="payment-method-grid">
                {subscriptionPaymentMethods.map((method) => <button type="button" key={method} className={subscriptionPaymentForm.payment_method === method ? "payment-method-card active" : "payment-method-card"} onClick={() => setSubscriptionPaymentForm((current) => ({ ...current, payment_method: method }))}><strong>{methodLabel(method)}</strong><span>{method === "PAYNOW" ? "Approve the request from an EcoCash or OneMoney phone." : "Submit a reference for confirmation."}</span></button>)}
              </div>
              {subscriptionPaymentMethods.length === 0 ? <div className="error-box">No package payment method is currently enabled. Contact TAZY support.</div> : null}
              {subscriptionPaymentForm.payment_method === "PAYNOW" ? <div className="paynow-checkout-fields"><div><label>Mobile wallet</label><select value={subscriptionPaymentForm.paynow_method} onChange={(e) => setSubscriptionPaymentForm((current) => ({ ...current, paynow_method: e.target.value }))}><option value="ecocash">EcoCash</option><option value="onemoney">OneMoney</option></select></div><div><label>Mobile number</label><input type="tel" required value={subscriptionPaymentForm.mobile} onChange={(e) => setSubscriptionPaymentForm((current) => ({ ...current, mobile: e.target.value }))} placeholder="0771234567" /><small>A payment approval request will be sent to this phone.</small></div></div> : <div className="modal-form-grid polished-form-grid payment-reference-grid"><div><label>Payment reference</label><input required value={subscriptionPaymentForm.payment_reference} onChange={(e) => setSubscriptionPaymentForm((current) => ({ ...current, payment_reference: e.target.value }))} placeholder="Bank or payment reference" /></div></div>}
              <div className="payment-modal-actions"><button type="button" onClick={() => setSubscriptionModal(null)}>Cancel</button><button type="submit" disabled={!subscriptionPaymentForm.payment_method || busy === `plan-${subscriptionModal.id}`}>{busy === `plan-${subscriptionModal.id}` ? "Starting payment..." : "Pay and activate package"}</button></div>
            </form>
          </section>
        </div>
      )}

      {payoutModal && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card payout-request-modal">
            <header className="tazy-modal-header"><div><span>PAYOUT REQUEST</span><h3>Withdraw available balance</h3></div><button type="button" onClick={() => setPayoutModal(false)}>×</button></header>
            <form className="finance-confirm-body" onSubmit={submitPayoutRequest}>
              <p className="payout-explainer">Only available wallet funds can be requested. The amount is reserved while finance reviews and completes the payout.</p>
              <div className="modal-form-grid polished-form-grid">
                <div><label>Wallet</label><select required value={payoutForm.wallet} onChange={(e) => setPayoutForm({ ...payoutForm, wallet: e.target.value })}><option value="">Choose wallet</option>{(payload.wallets || []).filter((wallet) => wallet.status === "ACTIVE").map((wallet) => <option key={wallet.id} value={wallet.id}>{wallet.code} — {money(wallet.available_balance)} available</option>)}</select></div>
                <div><label>Amount</label><input required type="number" min="0.01" step="0.01" value={payoutForm.amount} onChange={(e) => setPayoutForm({ ...payoutForm, amount: e.target.value })} placeholder="0.00" /></div>
                <div><label>Payout method</label><select value={payoutForm.payout_method} onChange={(e) => setPayoutForm({ ...payoutForm, payout_method: e.target.value })}><option value="ECOCASH">EcoCash</option><option value="ONEMONEY">OneMoney</option><option value="BANK_TRANSFER">Bank transfer</option><option value="CASH">Cash</option><option value="OTHER">Other</option></select></div>
                <div><label>Account name</label><input required value={payoutForm.account_name} onChange={(e) => setPayoutForm({ ...payoutForm, account_name: e.target.value })} placeholder="Registered account holder" /></div>
                <div className="wide-field"><label>Mobile number or bank destination</label><input required value={payoutForm.destination} onChange={(e) => setPayoutForm({ ...payoutForm, destination: e.target.value })} placeholder="Mobile number or bank account details" /></div>
                <div className="wide-field"><label>Request note</label><textarea rows="3" value={payoutForm.request_notes} onChange={(e) => setPayoutForm({ ...payoutForm, request_notes: e.target.value })} placeholder="Optional information for finance" /></div>
              </div>
              <div className="payment-modal-actions"><button type="button" onClick={() => setPayoutModal(false)}>Cancel</button><button type="submit" disabled={busy === "payout-create"}>{busy === "payout-create" ? "Submitting..." : "Submit payout request"}</button></div>
            </form>
          </section>
        </div>
      )}

      {payoutReview && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card finance-confirm-modal">
            <header className="tazy-modal-header"><div><span>PAYOUT WORKFLOW</span><h3>{payoutReview.code}</h3></div><button type="button" onClick={() => setPayoutReview(null)}>×</button></header>
            <form className="finance-confirm-body" onSubmit={decidePayout}>
              <div className="finance-confirm-summary">
                <div><span>Requester</span><strong>{payoutReview.requester_name}</strong></div>
                <div><span>Amount</span><strong>{money(payoutReview.amount)}</strong></div>
                <div><span>Method</span><strong>{methodLabel(payoutReview.payout_method)}</strong></div>
                <div><span>Destination</span><strong>{payoutReview.destination}</strong></div>
              </div>
              <div className="modal-form-grid polished-form-grid payment-reference-grid">
                {payoutReviewForm.action === "MARK_PAID" && <div><label>Payout reference</label><input required value={payoutReviewForm.payout_reference} onChange={(e) => setPayoutReviewForm({ ...payoutReviewForm, payout_reference: e.target.value })} placeholder="Bank, mobile-money or cash reference" /></div>}
                <div><label>{payoutReviewForm.action === "CANCEL" ? "Cancellation note" : "Review note"}</label><input value={payoutReviewForm.review_notes} onChange={(e) => setPayoutReviewForm({ ...payoutReviewForm, review_notes: e.target.value })} placeholder="Optional audit note" /></div>
              </div>
              <div className="payment-modal-actions"><button type="button" onClick={() => setPayoutReview(null)}>Back</button><button type="submit" disabled={busy === `payout-${payoutReview.id}`}>{busy === `payout-${payoutReview.id}` ? "Saving..." : payoutReviewForm.action === "MARK_PAID" ? "Confirm payout paid" : payoutReviewForm.action === "APPROVE" ? "Approve request" : payoutReviewForm.action === "REJECT" ? "Reject request" : "Cancel request"}</button></div>
            </form>
          </section>
        </div>
      )}

      {confirmTarget && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card finance-confirm-modal">
            <header className="tazy-modal-header"><div><span>PAYMENT REVIEW</span><h3>{confirmTarget.code}</h3></div><button type="button" onClick={() => setConfirmTarget(null)}>×</button></header>
            <form className="finance-confirm-body" onSubmit={confirmPayment}>
              <div className="finance-confirm-summary">
                <div><span>Order / purpose</span><strong>{confirmTarget.order_number || confirmTarget.subscription_plan_name || confirmTarget.payment_for}</strong></div>
                <div><span>Buyer / payer</span><strong>{confirmTarget.payer_name || "—"}</strong></div>
                <div><span>Seller / beneficiary</span><strong>{confirmTarget.seller_name || "TAZY PLATFORM"}</strong></div>
                <div><span>Amount</span><strong>{money(confirmTarget.amount)}</strong></div>
              </div>
              {confirmTarget.order_items_summary && <div className="finance-confirm-items">{confirmTarget.order_items_summary}</div>}
              <div className="modal-form-grid polished-form-grid payment-reference-grid">
                <div><label>Payment method</label><select value={confirmForm.payment_method} onChange={(e) => setConfirmForm({ ...confirmForm, payment_method: e.target.value })}>{paymentMethods.map((method) => <option key={method} value={method}>{methodLabel(method)}</option>)}</select></div>
                <div><label>Reference used by finance</label><input value={confirmForm.payment_reference} onChange={(e) => setConfirmForm({ ...confirmForm, payment_reference: e.target.value })} placeholder="Receipt, EcoCash, bank or POD reference" /></div>
              </div>
              <div className="payment-modal-actions"><button type="button" onClick={() => setConfirmTarget(null)}>Cancel</button><button type="submit" disabled={busy === `confirm-${confirmTarget.id}`}>{busy === `confirm-${confirmTarget.id}` ? "Confirming..." : "Confirm money received"}</button></div>
            </form>
          </section>
        </div>
      )}

      {settleTarget && (
        <div className="tazy-modal-backdrop" role="dialog" aria-modal="true">
          <section className="tazy-modal-card action-modal-card finance-confirm-modal">
            <header className="tazy-modal-header">
              <div>
                <span>SELLER SETTLEMENT</span>
                <h3>{settleTarget.code}</h3>
              </div>
              <button type="button" onClick={() => setSettleTarget(null)}>×</button>
            </header>
            <form className="finance-confirm-body" onSubmit={settleCommission}>
              <div className="finance-confirm-summary">
                <div><span>Order</span><strong>{settleTarget.order_number || "—"}</strong></div>
                <div><span>Seller</span><strong>{settleTarget.seller_name || "—"}</strong></div>
                <div><span>Gross amount</span><strong>{money(settleTarget.gross_amount)}</strong></div>
                <div><span>Amount to seller</span><strong>{money(settleTarget.seller_amount)}</strong></div>
              </div>
              <div className="modal-form-grid polished-form-grid payment-reference-grid">
                <div>
                  <label>Payout / settlement reference</label>
                  <input
                    value={settleForm.payout_reference}
                    onChange={(e) => setSettleForm({ ...settleForm, payout_reference: e.target.value })}
                    placeholder="Bank transfer, EcoCash, cash voucher or internal ref"
                  />
                </div>
                <div>
                  <label>Settlement note</label>
                  <input
                    value={settleForm.notes}
                    onChange={(e) => setSettleForm({ ...settleForm, notes: e.target.value })}
                    placeholder="Optional settlement note"
                  />
                </div>
              </div>
              <div className="payment-modal-actions">
                <button type="button" onClick={() => setSettleTarget(null)}>Cancel</button>
                <button type="submit" disabled={busy === `settle-${settleTarget.id}`}>
                  {busy === `settle-${settleTarget.id}` ? "Settling..." : "Release settlement"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

    </div>
  );
}
